import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { addIpIntrusionRule, UserKeys } from '../../../../api/users-api';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import { useStore } from '../../../state/zustand-store';
import {
  isAbleToSeeUIElement,
  AdministrationRoles,
} from '../../../../utils/roles/client-roles';
import UsersAutocomplete from '../../../../shared/users-autocomplete';

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface AddIpIntrusionAddressesDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
}

export default function AddIpIntrusionAddressesDialog({
  open,
  handleClose,
  changeLogRoles,
}: AddIpIntrusionAddressesDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [ipIntrusionRuleType, setIpIntrusionRuleType] = useState('ipAddress');
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  interface FormState {
    ipAddress: string;
    startIpAddress: string;
    endIpAddress: string;
    user: string;
  }

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormState>({
    defaultValues: {
      ipAddress: '',
      startIpAddress: '',
      endIpAddress: '',
      user: '',
    },
  });

  const onSubmit = (data: FormState) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.CreateIpIntrusionEntity.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormState) => {
      const value =
        ipIntrusionRuleType === 'ipRange'
          ? `${data.startIpAddress}-${data.endIpAddress}`
          : ipIntrusionRuleType === 'ipAddress'
          ? data.ipAddress
          : data.user;

      return addIpIntrusionRule(
        { value, type: ipIntrusionRuleType },
        changeLogMessage
      );
    },
    onSuccess: (returnedData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'IP Intrusion rule added successfully!',
      });

      queryClient.setQueryData([UserKeys.IpIntrusionSettings], returnedData);

      setChangeLogMessage('');

      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the IP Intrusion rule. Please refresh and try again.',
      });
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Add IP Intrusion Rule"
      dialogContent={
        <Stack gap={1}>
          <Box display="flex" justifyContent="center">
            <RadioGroup
              row
              value={ipIntrusionRuleType}
              onChange={(e) => {
                setIpIntrusionRuleType(e.target.value);
                setValue('user', '');
                setValue('ipAddress', '');
                setValue('startIpAddress', '');
                setValue('endIpAddress', '');
              }}
            >
              <FormControlLabel
                value="ipAddress"
                control={<Radio />}
                label="IP Address"
              />
              <FormControlLabel
                value="ipRange"
                control={<Radio />}
                label="IP Range"
              />
              <FormControlLabel value="user" control={<Radio />} label="User" />
            </RadioGroup>
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            {ipIntrusionRuleType === 'ipAddress' && (
              <Controller
                rules={{
                  required: 'An address is required.',
                  validate: (value) =>
                    !value
                      .trim()
                      .match(
                        /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
                      )
                      ? 'Invalid IP Address'
                      : undefined,
                }}
                name="ipAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="IP Address"
                    variant="outlined"
                    fullWidth
                    error={!!errors.ipAddress}
                    helperText={errors.ipAddress?.message ?? ' '}
                  />
                )}
              />
            )}
            {ipIntrusionRuleType === 'ipRange' && (
              <Stack flexDirection="row" alignItems="baseline" gap={2}>
                <Controller
                  rules={{
                    required: 'An address is required.',
                    validate: (value) =>
                      !value
                        .trim()
                        .match(
                          /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
                        )
                        ? 'Invalid IP Address'
                        : undefined,
                  }}
                  name="startIpAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Start IP"
                      variant="outlined"
                      fullWidth
                      error={!!errors.startIpAddress}
                      helperText={errors.startIpAddress?.message ?? ' '}
                    />
                  )}
                />
                <Typography>-</Typography>
                <Controller
                  rules={{
                    required: 'An address is required.',
                    validate: (value) =>
                      !value
                        .trim()
                        .match(
                          /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
                        )
                        ? 'Invalid IP Address'
                        : undefined,
                  }}
                  name="endIpAddress"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="End IP"
                      variant="outlined"
                      fullWidth
                      error={!!errors.endIpAddress}
                      helperText={errors.endIpAddress?.message ?? ' '}
                    />
                  )}
                />
              </Stack>
            )}
            {ipIntrusionRuleType === 'user' && (
              <Controller
                key={'user'}
                rules={{
                  required: 'A user is required.',
                }}
                name="user"
                control={control}
                render={({ field }) => (
                  <Box
                    sx={{
                      width: '100%',
                    }}
                  >
                    <UsersAutocomplete
                      variant="outlined"
                      freeSolo
                      autocompleteValue={field.value}
                      setAutoCompleteValue={(newValue) =>
                        field.onChange(newValue)
                      }
                      errors={!!errors.user}
                      helperText={errors.user?.message ?? ' '}
                    />
                  </Box>
                )}
              />
            )}
          </Box>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(getValues());
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty,
        title: 'Add Rule',
      }}
    />
  );
}
